import { QueryClient } from "@tanstack/react-query";

import { DuplicatedQuoteRequestLine } from "#modules/quoteRequestLines/infra/duplicateQuoteRequestLine.api";
import {
  getQuoteRequestLineQueryData,
  setQuoteRequestLineQueryData,
} from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { getQuoteRequestQueryData } from "#modules/quoteRequests/infra/quoteRequest.query";

type ApplyDuplicateQuoteRequestLineToCacheParams = {
  queryClient: QueryClient;
  duplicatedQuoteRequestLine: DuplicatedQuoteRequestLine;
  sourceId: string;
  useBatch: boolean;
};

export const applyDuplicateQuoteRequestLineToCache = ({
  queryClient,
  duplicatedQuoteRequestLine,
  sourceId,
  useBatch,
}: ApplyDuplicateQuoteRequestLineToCacheParams) => {
  const quoteRequest = getQuoteRequestQueryData(
    queryClient,
    duplicatedQuoteRequestLine.quoteRequestId,
  );

  if (useBatch) {
    applyBatchIdToSourceLine({
      queryClient,
      sourceId,
      batchId: duplicatedQuoteRequestLine.batchId,
    });
    return;
  }

  const siblingIds = quoteRequest?.quoteRequestLines.map((line) => line.id) ?? [];

  shiftSiblingIndexes({
    queryClient,
    duplicatedQuoteRequestLine,
    siblingIds,
  });
};

const applyBatchIdToSourceLine = ({
  queryClient,
  sourceId,
  batchId,
}: {
  queryClient: QueryClient;
  sourceId: string;
  batchId: string | null;
}) => {
  if (!batchId) {
    return;
  }

  const sourceQuoteRequestLine = getQuoteRequestLineQueryData(queryClient, sourceId);

  if (sourceQuoteRequestLine && !sourceQuoteRequestLine.batchId) {
    setQuoteRequestLineQueryData(queryClient, sourceId, {
      ...sourceQuoteRequestLine,
      batchId,
    });
  }
};

const shiftSiblingIndexes = ({
  queryClient,
  duplicatedQuoteRequestLine,
  siblingIds,
}: {
  queryClient: QueryClient;
  duplicatedQuoteRequestLine: DuplicatedQuoteRequestLine;
  siblingIds: string[];
}) => {
  for (const lineId of siblingIds) {
    if (lineId === duplicatedQuoteRequestLine.id) {
      continue;
    }

    const line = getQuoteRequestLineQueryData(queryClient, lineId);

    if (!line) {
      continue;
    }

    if (line.quoteRequestId !== duplicatedQuoteRequestLine.quoteRequestId) {
      continue;
    }

    if (line.assemblyParentId !== duplicatedQuoteRequestLine.assemblyParentId) {
      continue;
    }

    if (line.index >= duplicatedQuoteRequestLine.index) {
      setQuoteRequestLineQueryData(queryClient, lineId, {
        ...line,
        index: line.index + 1,
      });
    }
  }
};
